import React, { useEffect, useMemo, useState } from 'react';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { IBusinessProfile } from '../../../Interfaces/responseTypes';
import { getOrderTimePickerBounds, isValidOrderTimeStep, ORDER_TIME_STEP_MINUTES } from '../../../Utilits/utilits';
import AuthError from '../../AuthError/authError';
import styles from '../AuthInput/authInputs.module.scss';

interface ICustomTimePickerProps {
    label?: string;
    value: Date | null;
    onChange: (value: Date | null) => void;
    error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
    disabled?: boolean;
    id?: string;
    showTextError?: boolean;
    placeholder?: string;
    style?: string;
    labelStyle?: string;
    startTime?: string | null;
    endTime?: string | null;
    selectedDate?: string | null;
    businessProfile?: IBusinessProfile | null;
}

const CustomTimePicker: React.FC<ICustomTimePickerProps> = ({
    label,
    value,
    onChange,
    error,
    disabled,
    id,
    showTextError = true,
    placeholder,
    style,
    labelStyle,
    startTime,
    endTime,
    selectedDate,
    businessProfile,
}) => {
    const [open, setOpen] = useState<boolean>(false);

    const parseTime = (timeStr?: string | null): Date | undefined => {
        if (!timeStr) return undefined;
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        const now = new Date();
        now.setHours(hours, minutes, seconds || 0, 0);
        return now;
    };

    const isToday = (): boolean => {
        if (!selectedDate) return false;
        const today = new Date();
        const selected = new Date(`${selectedDate}T12:00:00`);
        return (
            today.getFullYear() === selected.getFullYear()
            && today.getMonth() === selected.getMonth()
            && today.getDate() === selected.getDate()
        );
    };

    const legacyBounds = useMemo(() => {
        if (isToday()) {
            const now = new Date();
            now.setMinutes(now.getMinutes() + 1, 0, 0);
            return {
                minTime: now,
                maxTime: endTime ? parseTime(endTime) : undefined,
            };
        }

        return {
            minTime: startTime ? parseTime(startTime) : undefined,
            maxTime: endTime ? parseTime(endTime) : undefined,
        };
    }, [endTime, selectedDate, startTime]);

    const orderBounds = useMemo(
        () => getOrderTimePickerBounds(businessProfile, selectedDate),
        [businessProfile, selectedDate],
    );

    const useOrderHours = Boolean(businessProfile);
    const minTime = useOrderHours
        ? (orderBounds.hasValidWindow ? orderBounds.minTime : undefined)
        : legacyBounds.minTime;
    const maxTime = useOrderHours
        ? (orderBounds.hasValidWindow ? orderBounds.maxTime : undefined)
        : legacyBounds.maxTime;
    const isPickerDisabled = disabled
        || (useOrderHours && (orderBounds.isClosedDay || !orderBounds.hasValidWindow));

    const isTimeWithinBounds = (time: Date): boolean => {
        if (minTime && time < minTime) {
            return false;
        }
        if (maxTime && time > maxTime) {
            return false;
        }
        return true;
    };

    const handleTimeChange = (newValue: Date | null) => {
        if (!newValue) {
            onChange(null);
            return;
        }

        const normalized = new Date(newValue);
        normalized.setSeconds(0, 0);
        normalized.setMilliseconds(0);

        if (useOrderHours && !isValidOrderTimeStep(normalized)) {
            return;
        }

        if (!isTimeWithinBounds(normalized)) {
            return;
        }

        onChange(normalized);
    };

    const shouldDisableTime = (timeValue: Date, view: 'hours' | 'minutes' | 'seconds') => {
        if (!useOrderHours) {
            return false;
        }

        if (view === 'minutes') {
            return !isValidOrderTimeStep(timeValue);
        }

        if (view === 'hours') {
            const hour = timeValue.getHours();
            const minuteSlots = Array.from(
                { length: 60 / ORDER_TIME_STEP_MINUTES },
                (_, index) => index * ORDER_TIME_STEP_MINUTES,
            );
            const hasValidMinute = minuteSlots.some((minute) => {
                const candidate = new Date(timeValue);
                candidate.setHours(hour, minute, 0, 0);
                return isValidOrderTimeStep(candidate) && isTimeWithinBounds(candidate);
            });
            return !hasValidMinute;
        }

        return false;
    };

    useEffect(() => {
        if (!useOrderHours || !value || !selectedDate) {
            return;
        }

        if (orderBounds.isClosedDay || !orderBounds.hasValidWindow) {
            onChange(null);
            return;
        }

        if (!isValidOrderTimeStep(value)) {
            onChange(null);
            return;
        }

        if (minTime && value < minTime) {
            onChange(null);
            return;
        }

        if (maxTime && value > maxTime) {
            onChange(null);
        }
    }, [
        maxTime,
        minTime,
        onChange,
        orderBounds.hasValidWindow,
        orderBounds.isClosedDay,
        selectedDate,
        useOrderHours,
        value,
    ]);

    return (
        <div className={`${styles.container} ${style}`}>
            {label && (
                <label htmlFor={id} className={`${styles.label} ${labelStyle}`}>
                    {label}
                    {error && <span style={{ color: '#b00020', marginLeft: '5px' }}>*</span>}
                </label>
            )}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopTimePicker
                    disabled={isPickerDisabled}
                    value={value}
                    onChange={handleTimeChange}
                    ampm={false}
                    open={open}
                    onOpen={() => !isPickerDisabled && setOpen(true)}
                    onClose={() => setOpen(false)}
                    views={['hours', 'minutes']}
                    minTime={minTime}
                    maxTime={maxTime}
                    minutesStep={ORDER_TIME_STEP_MINUTES}
                    shouldDisableTime={shouldDisableTime}
                    timeSteps={{ minutes: ORDER_TIME_STEP_MINUTES }}
                    slotProps={{
                        textField: {
                            placeholder,
                            id,
                            fullWidth: true,
                            error: !!error,
                            helperText: '',
                            onClick: (e) => {
                                e.stopPropagation();
                                if (!isPickerDisabled) {
                                    setOpen(true);
                                }
                            },
                            inputProps: {
                                readOnly: true,
                                helperText: '',
                                placeholder: '',
                            },
                            sx: {
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: 'transparent' },
                                    '&:hover fieldset': { borderColor: 'var(--text-dark)' },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'var(--text-dark)',
                                        borderWidth: '1px',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: 'var(--input-label)',
                                        transform: "translateX(-5px)"
                                    },
                                    ...(error && {
                                        '& fieldset': { borderColor: '#b00020 !important' },
                                        '&:hover fieldset': { borderColor: '#b00020 !important' },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#b00020 !important',
                                            borderWidth: '1px',
                                        },
                                    }),
                                    '& .MuiInputBase-input': {
                                        padding: '16px 10px',
                                        fontSize: '13px',
                                        fontWeight: "400",
                                        '@media screen and (max-height:800px)': {
                                            padding: '10px 5px',
                                        },
                                          '@media screen and (max-width:800px)': {
                                            padding: '10px 5px',
                                        },
                                        '@media (max-width: 400px)': {
                                            padding: '12px 5px',
                                            fontSize: '12px'
                                        },

                                    },
                                },
                            },
                        },
                        popper: {
                            sx: {
                                '& .MuiPickersLayout-root': {
                                    backgroundColor: 'var(--background-color)',

                                    '& .MuiButtonBase-root': {
                                        color: 'var(--text-dark)',
                                    },
                                },
                                '& .MuiButtonBase-root.Mui-selected': {
                                    backgroundColor: 'var(--primary-hover) !important',
                                    color: 'white !important',
                                },
                                '& .MuiClock-root': {
                                    backgroundColor: 'var(--background-color)',
                                    borderRadius: '16px',
                                },
                                '& .MuiClockNumber-root': {
                                    color: 'var(--text-dark)',
                                    fontSize: '14px',
                                    '&.Mui-selected': {
                                        backgroundColor: 'var(--primary-hover)',
                                        color: '#fff',
                                    },
                                },
                                '& .MuiClockPointer-root': {
                                    backgroundColor: 'var(--primary-hover)',
                                },
                                '& .MuiClockPointer-thumb': {
                                    borderColor: 'var(--primary-hover)',
                                    backgroundColor: 'var(--text-dark)',
                                },
                                '& .MuiClock-pin': {
                                    backgroundColor: 'var(--text-dark)',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.2)',
                                },
                            },
                        },
                    }}
                />
            </LocalizationProvider>
            {showTextError && <AuthError text={error?.message} />}
        </div>
    );
};

export default CustomTimePicker;
